import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { SITE } from "../lib/site";
import { Mail, Phone, MapPin, Clock, ArrowUpRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 lg:py-36 bg-black"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          {/* Left */}
          <div className="lg:col-span-5 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-gold" />
              <span className="text-xs uppercase tracking-[0.32em] text-gold/90">
                Get In Touch
              </span>
            </div>
            <h2 className="font-display font-light text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Ready to give your car a
              <span className="italic gold-shimmer"> refresh</span>?
            </h2>
            <p className="mt-7 text-white/60 leading-relaxed max-w-md">
              Book directly online, or send us a message and we&apos;ll get
              back to you with a quote.
            </p>

            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-book-online"
              className="mt-10 inline-flex items-center gap-3 px-7 py-4 bg-gold text-black rounded-full text-sm uppercase tracking-[0.22em] font-medium hover:bg-[#F3E5AB] transition-all"
            >
              Book Online
              <ArrowUpRight size={18} />
            </a>

            <div className="mt-14 space-y-6">
              <InfoRow icon={MapPin} label="Service Area" value={SITE.area} />
              <InfoRow icon={Clock} label="Hours" value={SITE.hours} />
              <InfoRow icon={Mail} label="Email" value={SITE.email} />
              <InfoRow icon={Phone} label="Phone" value={SITE.phone} />
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="relative bg-[#0a0a0a] border border-white/10 p-8 lg:p-12 rounded-sm"
            >
              <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field
                  label="Name"
                  name="name"
                  testid="contact-input-name"
                  value={form.name}
                  onChange={onChange}
                  required
                />
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  testid="contact-input-email"
                  value={form.email}
                  onChange={onChange}
                  required
                />
                <Field
                  label="Phone"
                  name="phone"
                  testid="contact-input-phone"
                  value={form.phone}
                  onChange={onChange}
                />
                <Field
                  label="Service of interest"
                  name="service"
                  testid="contact-input-service"
                  value={form.service}
                  onChange={onChange}
                  placeholder="e.g. Interior Revival"
                />
              </div>

              <div className="mt-6">
                <Label
                  htmlFor="message"
                  className="text-xs uppercase tracking-[0.22em] text-white/60"
                >
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={onChange}
                  rows={5}
                  required
                  className="gold-input mt-3 bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-0"
                  placeholder="Tell us about your car and what you're looking for..."
                />
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-xs text-white/40 max-w-sm">
                  By submitting you agree we may contact you about your inquiry.
                </p>
                <Button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="bg-gold text-black hover:bg-[#F3E5AB] rounded-full px-8 py-6 uppercase tracking-[0.22em] text-xs font-medium"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", testid, value, onChange, required, placeholder, autoComplete }) {
  return (
    <div>
      <Label
        htmlFor={name}
        className="text-xs uppercase tracking-[0.22em] text-white/60"
      >
        {label}
        {required && <span className="text-gold ml-1">*</span>}
      </Label>
      <Input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete || "off"}
        data-testid={testid}
        className="gold-input mt-3 bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-0 h-12"
      />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <span className="h-10 w-10 shrink-0 grid place-items-center border border-gold/40 rounded-full text-gold">
        <Icon size={16} />
      </span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-white/45">
          {label}
        </div>
        <div className="font-display text-lg text-white mt-1">{value}</div>
      </div>
    </div>
  );
}
